/** Mutations on the scaffold model, plus its JSON serialisation. */

import { ALP_LENGTHS, GUARDRAIL_TOP_ABOVE_NODE, MM, nodeY, SPIRE_LENGTHS } from "../data/haki.ts";
import {
  bayEdges,
  bayFrame,
  beamKey,
  bracketUnder,
  dist,
  edgeKey,
  findBeam,
  findEdge,
  kXZ,
  legAt,
  near,
  nearestMod,
  nearestOf,
} from "./geometry.ts";
import { baseLevel, legHeight } from "./solver.ts";
import type {
  Beam,
  BeamKind,
  Board,
  Bracket,
  Deck,
  Diagonal,
  Ladder,
  Leg,
  Point,
  Rail,
  ScaffoldModel,
  Stair,
  StairRail,
  ToeBoard,
} from "./types.ts";
import { isMounted, isSmallStair, isUtvStair } from "./types.ts";

export function addLeg(model: ScaffoldModel, x: number, z: number, segments: number[], gy?: number): Leg {
  const key = kXZ(x, z);
  const existing = model.legs.get(key);
  if (existing) {
    return existing;
  }

  const leg: Leg = { x: +x.toFixed(3), z: +z.toFixed(3), segments };
  if (gy !== undefined) {
    leg.gy = gy;
  }
  model.legs.set(key, leg);

  return leg;
}

/**
 * The standard on a bracket's spigot. Its base is the bracket's cup level, so
 * the base level is known without a terrain cast. Starts as the bare stub.
 */
export function addMountedLeg(model: ScaffoldModel, at: Point, mountP: number, segments: number[]): Leg {
  const key = kXZ(at.x, at.z);
  const existing = model.legs.get(key);
  if (existing) {
    return existing;
  }

  const leg: Leg = {
    x: +at.x.toFixed(3),
    z: +at.z.toFixed(3),
    segments,
    mountP,
    baseP: mountP,
    gy: nodeY(mountP),
  };
  model.legs.set(key, leg);

  return leg;
}

/** Place a bracket, and the mounted standard — initially just its stub — at the end. */
export function addBracket(model: ScaffoldModel, bracket: Bracket): Leg {
  model.brackets.push({
    kind: bracket.kind,
    a: { x: bracket.a.x, z: bracket.a.z },
    b: { x: bracket.b.x, z: bracket.b.z },
    p: bracket.p,
    len: bracket.len,
  });

  return addMountedLeg(model, bracket.b, bracket.p, []);
}

/**
 * Remove a standard and everything attached to it — including the brackets
 * hooked onto it and, through them, the standards on their spigots.
 *
 * Only `removeStandard` and the pruning code should call this on a mounted
 * standard: removing one directly would leave its bracket with no cup group.
 */
export function removeLeg(model: ScaffoldModel, leg: Point): void {
  model.legs.delete(kXZ(leg.x, leg.z));
  model.beams = model.beams.filter((b) => !(near(b.a, leg) || near(b.b, leg)));
  model.diags = model.diags.filter((d) => !(near(d.a, leg) || near(d.b, leg)));
  model.decks = model.decks.filter((d) => !d.c.some((c) => near(c, leg)));
  // UTV ×2000 stairs hang on transoms, not on legs, so they are left to the
  // render-time prune (`pruneUnsupported`) once their beams have gone.
  model.stairs = model.stairs.filter((s) => !isSmallStair(s) || !(near(s.a, leg) || near(s.b, leg)));
  model.ladders = model.ladders.filter((l) => !(near(l.a, leg) || near(l.b, leg)));
  model.rails = model.rails.filter((r) => !(near(r.a, leg) || near(r.b, leg)));
  model.toes = model.toes.filter((t) => !(near(t.a, leg) || near(t.b, leg)));

  for (const bracket of model.brackets.filter((k) => near(k.a, leg))) {
    removeBracket(model, bracket);
  }
}

/** Remove a bracket together with the standard on its spigot and all that hangs on it. */
export function removeBracket(model: ScaffoldModel, bracket: Bracket): void {
  model.brackets = model.brackets.filter((k) => k !== bracket);

  const mounted = legAt(model, bracket.b.x, bracket.b.z);
  if (mounted && mounted.mountP === bracket.p) {
    removeLeg(model, mounted);
  }
}

/**
 * What "delete this standard" means: a ground standard goes with everything
 * hooked onto it, while a standard on a bracket is stripped back to the
 * bracket's stub — the bracket itself stays.
 */
export function removeStandard(model: ScaffoldModel, leg: Leg): void {
  if (isMounted(leg)) {
    leg.segments = [];
    return;
  }

  removeLeg(model, leg);
}

export function addBeam(
  model: ScaffoldModel,
  a: Point,
  b: Point,
  p: number,
  kind: BeamKind,
  len?: number,
): boolean {
  if (findBeam(model, a, b, p)) {
    return false;
  }

  model.beams.push({
    a: { x: a.x, z: a.z },
    b: { x: b.x, z: b.z },
    p,
    kind,
    len: len ?? nearestMod(dist(a, b) * 1000),
  });

  return true;
}

/* -----------------------------------------------------------------------------
   Loose boards
   ----------------------------------------------------------------------------- */

/** Board length as the component list books it: the nearest ALP length to its span. */
export function boardLength(board: Board): number {
  return nearestOf(ALP_LENGTHS, dist(board.a, board.b) * 1000);
}

/**
 * Would this board land on top of another board or a deck at its level?
 * Boards are compared as parallel strips: centrelines closer than the two
 * half-widths, overlapping along their length.
 */
export function boardBlocked(model: ScaffoldModel, board: Board): boolean {
  const mx = (board.a.x + board.b.x) / 2;
  const mz = (board.a.z + board.b.z) / 2;
  const len = dist(board.a, board.b) || 1;
  const dx = (board.b.x - board.a.x) / len;
  const dz = (board.b.z - board.a.z) / len;

  for (const other of model.boards) {
    if (other === board || other.p !== board.p) {
      continue;
    }

    const otherLen = dist(other.a, other.b) || 1;
    const ox = (other.b.x - other.a.x) / otherLen;
    const oz = (other.b.z - other.a.z) / otherLen;
    if (Math.abs(dx * ox + dz * oz) < 0.98) {
      continue;
    }

    const ex = (other.a.x + other.b.x) / 2 - mx;
    const ez = (other.a.z + other.b.z) / 2 - mz;
    const along = Math.abs(ex * dx + ez * dz);
    const across = Math.abs(ex * -dz + ez * dx);
    if (along < (len + otherLen) / 2 - 0.05 && across < ((board.wid + other.wid) / 2) * MM - 0.005) {
      return true;
    }
  }

  return model.decks.some((deck) => {
    if (deck.p !== board.p) {
      return false;
    }

    const frame = bayFrame(deck.c);
    const local = frame.toLocal({ x: mx, z: mz });

    return local.x > 0 && local.x < frame.L && local.z > 0 && local.z < frame.W;
  });
}

export function addBoard(model: ScaffoldModel, board: Board): void {
  model.boards.push({
    a: { x: board.a.x, z: board.a.z },
    b: { x: board.b.x, z: board.b.z },
    p: board.p,
    wid: board.wid,
  });
}

export function findRail(model: ScaffoldModel, a: Point, b: Point, p: number): Rail | undefined {
  const key = edgeKey(a, b, p);
  return model.rails.find((r) => edgeKey(r.a, r.b, r.p) === key);
}

export function findToe(model: ScaffoldModel, a: Point, b: Point, p: number): ToeBoard | undefined {
  const key = edgeKey(a, b, p);
  return model.toes.find((t) => edgeKey(t.a, t.b, t.p) === key);
}

/** Inward direction for a toe board: toward the deck sharing the edge, else toward `hint`. */
export function inwardFor(
  model: ScaffoldModel,
  a: Point,
  b: Point,
  p: number,
  hint?: Point,
): { inX: number; inZ: number } {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz) || 1;
  const px = -dz / len;
  const pz = dx / len;
  const mx = (a.x + b.x) / 2;
  const mz = (a.z + b.z) / 2;

  const deck = model.decks.find(
    (d) => d.p === p && d.c.some((c) => near(c, a)) && d.c.some((c) => near(c, b)),
  );
  const tx = deck ? (deck.c[0].x + deck.c[3].x) / 2 : (hint?.x ?? mx + px);
  const tz = deck ? (deck.c[0].z + deck.c[3].z) / 2 : (hint?.z ?? mz + pz);
  const sgn = (tx - mx) * px + (tz - mz) * pz >= 0 ? 1 : -1;

  return { inX: px * sgn, inZ: pz * sgn };
}

/** Guardrails and toe boards on every open deck edge. Used by the generator only. */
export function autoRailsFromDecks(model: ScaffoldModel): void {
  const eKey = (p: Point, q: Point) => [kXZ(p.x, p.z), kXZ(q.x, q.z)].sort().join("|");

  for (const deck of model.decks) {
    const [A, B, C, D] = deck.c;
    const shared = model.decks
      .filter((other) => other !== deck && other.p === deck.p)
      .flatMap((other) => {
        const [oa, ob, oc, od] = other.c;
        return [eKey(oa, ob), eKey(oc, od), eKey(oa, oc), eKey(ob, od)];
      });

    for (const [P, Q] of [
      [A, B],
      [C, D],
      [A, C],
      [B, D],
    ] as const) {
      if (shared.includes(eKey(P, Q))) {
        continue;
      }

      if (!findRail(model, P, Q, deck.p)) {
        model.rails.push({ a: { x: P.x, z: P.z }, b: { x: Q.x, z: Q.z }, p: deck.p });
      }
      if (!findToe(model, P, Q, deck.p)) {
        const inward = inwardFor(model, P, Q, deck.p);
        model.toes.push({ a: { x: P.x, z: P.z }, b: { x: Q.x, z: Q.z }, p: deck.p, ...inward });
      }
    }
  }
}

/**
 * Extend the standards carrying a guardrail until they reach its top connector.
 *
 * An SKRD frame hooks the standard at both ends, so a standard that stops below
 * the frame's upper hook leaves the guardrail attached to nothing. The generator
 * used to leave 1000 mm above the top lift while the connector needs 1436 mm, so
 * every top-lift guardrail was 436 mm short.
 *
 * The shortest single standard that closes the gap is added — 500 mm in the
 * common case. Returns how many segments were added.
 */
export function ensureGuardrailPosts(
  model: ScaffoldModel,
  groundAt: (x: number, z: number) => number,
): number {
  const ascending = [...SPIRE_LENGTHS].sort((a, b) => a - b);
  const longest = ascending[ascending.length - 1] ?? 3000;
  let added = 0;

  for (const rail of model.rails) {
    const required = nodeY(rail.p) + GUARDRAIL_TOP_ABOVE_NODE;

    for (const end of [rail.a, rail.b]) {
      const leg = model.legs.get(kXZ(end.x, end.z));
      if (!leg) {
        continue;
      }

      let top = nodeY(baseLevel(leg, groundAt)) + legHeight(leg);

      while (top < required - 1e-6) {
        const gapMm = (required - top) * 1000;
        const segment = ascending.find((length) => length >= gapMm) ?? longest;
        leg.segments.push(segment);
        top += segment * MM;
        added++;
      }
    }
  }

  return added;
}

export function clearModel(model: ScaffoldModel): void {
  model.legs.clear();
  model.beams = [];
  model.diags = [];
  model.decks = [];
  model.stairs = [];
  model.ladders = [];
  model.rails = [];
  model.toes = [];
  model.brackets = [];
  model.boards = [];
}

/* -----------------------------------------------------------------------------
   Serialisation
   ----------------------------------------------------------------------------- */

/** A deck as stored in JSON — `removed` is a Set at runtime, an array on disk. */
type SerializedDeck = Omit<Deck, "removed"> & { removed: number[] };

export type SerializedModel = {
  legs: Leg[];
  beams: Beam[];
  diags: Diagonal[];
  stairs: Stair[];
  ladders: Ladder[];
  rails: Rail[];
  toes: ToeBoard[];
  decks: SerializedDeck[];
  /** Absent in designs saved before brackets existed. */
  brackets?: Bracket[];
  /** Absent in designs saved before loose boards existed. */
  boards?: Board[];
  settings?: Record<string, unknown>;
};

export function serialize(model: ScaffoldModel, settings?: Record<string, unknown>): SerializedModel {
  return {
    legs: [...model.legs.values()],
    beams: model.beams,
    diags: model.diags,
    stairs: model.stairs,
    ladders: model.ladders,
    rails: model.rails,
    toes: model.toes,
    decks: model.decks.map((d) => ({ ...d, removed: [...d.removed] })),
    brackets: model.brackets,
    boards: model.boards,
    ...(settings ? { settings } : {}),
  };
}

/**
 * Handrails were once a bare side (`l` / `r` / `both`) with HLI implied only by
 * `both`. Map those onto the four-state cycle so older designs still open.
 */
function migrateStairRail<T extends Stair>(stair: T): T {
  const legacy: Record<string, StairRail> = { l: "hliLeft", r: "hliRight", both: "hlBoth" };
  const rail: unknown = stair.rail;

  if (typeof rail === "string" && rail in legacy) {
    return { ...stair, rail: legacy[rail] };
  }

  return stair;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Rebuild the model from parsed JSON. Legacy fields are tolerated: stairs in
 * the old bay format carry neither `utv` nor `small` and are dropped.
 */
export function deserialize(model: ScaffoldModel, raw: unknown): void {
  clearModel(model);
  if (!isRecord(raw)) {
    return;
  }

  for (const leg of asArray<Leg>(raw.legs)) {
    const restored: Leg = { x: leg.x, z: leg.z, segments: leg.segments };
    if (leg.gy !== undefined) {
      restored.gy = leg.gy;
    }
    if (typeof leg.mountP === "number") {
      restored.mountP = leg.mountP;
      restored.baseP = leg.mountP;
      restored.gy = nodeY(leg.mountP);
    }
    model.legs.set(kXZ(leg.x, leg.z), restored);
  }

  model.beams = asArray<Beam>(raw.beams);
  model.diags = asArray<Diagonal>(raw.diags);
  model.stairs = asArray<Stair>(raw.stairs)
    .filter((s) => isUtvStair(s) || isSmallStair(s))
    .map(migrateStairRail);
  model.ladders = asArray<Ladder>(raw.ladders);
  model.rails = asArray<Rail>(raw.rails);
  model.toes = asArray<ToeBoard>(raw.toes);
  model.decks = asArray<SerializedDeck>(raw.decks).map((d) => ({
    ...d,
    removed: new Set(d.removed ?? []),
  }));
  model.brackets = asArray<Bracket>(raw.brackets);
  model.boards = asArray<Board>(raw.boards);
}

/** Is a UTV ×2000 flight arriving at level `p` covering this point? */
export function stairCovers(model: ScaffoldModel, point: Point, p: number, utvWidth: number): boolean {
  const halfWidth = (utvWidth * MM) / 2 + 0.04;

  for (const stair of model.stairs) {
    if (!isUtvStair(stair) || stair.p + 4 !== p) {
      continue;
    }

    const dx = point.x - stair.o.x;
    const dz = point.z - stair.o.z;
    const along = dx * stair.dx + dz * stair.dz;
    const across = dx * -stair.dz + dz * stair.dx;
    if (along > -0.05 && along < stair.utv * MM + 0.05 && Math.abs(across) <= halfWidth) {
      return true;
    }
  }

  return false;
}

/** Does a stair entry/exit zone block a guardrail or toe board here? */
export function railBlocked(model: ScaffoldModel, point: Point, p: number, utvWidth: number): boolean {
  const halfWidth = (utvWidth * MM) / 2 + 0.05;

  for (const stair of model.stairs) {
    if (isUtvStair(stair)) {
      const dx = point.x - stair.o.x;
      const dz = point.z - stair.o.z;
      const along = dx * stair.dx + dz * stair.dz;
      if (Math.abs(dx * -stair.dz + dz * stair.dx) > halfWidth) {
        continue;
      }
      if (stair.p + 4 === p && along > -0.1 && along < stair.utv * MM + 0.1) {
        return true;
      }
      if (stair.p === p && along > -0.25 && along < 0.7) {
        return true;
      }
      continue;
    }

    if (stair.p !== p) {
      continue;
    }

    const mx = (stair.a.x + stair.b.x) / 2;
    const mz = (stair.a.z + stair.b.z) / 2;
    const dx = point.x - mx;
    const dz = point.z - mz;
    const along = dx * stair.dx + dz * stair.dz;
    if (Math.abs(dx * -stair.dz + dz * stair.dx) <= halfWidth && along > -0.6 && along < 0.4) {
      return true;
    }
  }

  return false;
}

/** Is `point` on the segment a–b, within `tolerance` metres? */
function onSegment(a: Point, b: Point, point: Point, tolerance: number): boolean {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) {
    return false;
  }

  const t = ((point.x - a.x) * dx + (point.z - a.z) * dz) / (len * len);
  if (t < -0.02 || t > 1.02) {
    return false;
  }

  const across = (point.x - a.x) * (-dz / len) + (point.z - a.z) * (dx / len);

  return Math.abs(across) <= tolerance;
}

/** Is there an edge under each end of the board at its level? */
function boardSupported(model: ScaffoldModel, board: Board): boolean {
  const edges = bayEdges(model).filter((edge) => edge.p === board.p);

  return [board.a, board.b].every((end) => edges.some((edge) => onSegment(edge.a, edge.b, end, 0.06)));
}

/** Both transoms a UTV ×2000 latches onto still present? */
function utvSupported(model: ScaffoldModel, stair: Stair): boolean {
  if (!isUtvStair(stair)) {
    return true;
  }

  const top: Point = {
    x: stair.o.x + stair.dx * stair.utv * MM,
    z: stair.o.z + stair.dz * stair.utv * MM,
  };
  const hasLower = model.beams.some((b) => b.p === stair.p && onSegment(b.a, b.b, stair.o, 0.06));
  const hasUpper = model.beams.some((b) => b.p === stair.p + 4 && onSegment(b.a, b.b, top, 0.06));

  return hasLower && hasUpper;
}

/** Four legs and four edges — beams or SK bracket arms — at the deck's level. */
function deckSupported(model: ScaffoldModel, deck: Deck): boolean {
  const [a, b, c, d] = deck.c;

  return (
    deck.c.every((corner) => model.legs.has(kXZ(corner.x, corner.z))) &&
    findEdge(model, a, b, deck.p) !== undefined &&
    findEdge(model, c, d, deck.p) !== undefined &&
    findEdge(model, a, c, deck.p) !== undefined &&
    findEdge(model, b, d, deck.p) !== undefined
  );
}

/**
 * Drop parts whose supports are gone. Called once at the top of every render —
 * the only place rendering is allowed to mutate the model.
 *
 * Brackets go first (their standard is gone), then mounted standards whose
 * bracket is gone, so the decks and stairs below see the final leg set.
 */
export function pruneUnsupported(model: ScaffoldModel): void {
  for (const bracket of model.brackets.filter((k) => !model.legs.has(kXZ(k.a.x, k.a.z)))) {
    removeBracket(model, bracket);
  }
  for (const leg of [...model.legs.values()]) {
    if (leg.mountP !== undefined && !bracketUnder(model, leg, leg.mountP)) {
      removeLeg(model, leg);
    }
  }

  model.decks = model.decks.filter((d) => deckSupported(model, d));
  model.boards = model.boards.filter((b) => boardSupported(model, b));
  model.stairs = model.stairs.filter((s) => utvSupported(model, s));
}

export { beamKey };
